import React, { useState } from 'react';
import {
    Button,
    Divider,
    Group as MGroup,
    NumberInput,
    Popover,
    SegmentedControl,
    Stack as MStack,
    Text as MText,
} from '@mantine/core';

import type { RateBasis } from '../../api/inventory';
import { dataSheetTokens } from './DataSheet';

/* ------------------------------------------------------------------ */
/*  DoH/MoH rate-basis control (Forecast vs Past Sales + DRR window)   */
/*  Explore-then-commit: drafts preview per-request, Save writes the   */
/*  org default. Shared by the Inventory tab and the MRP Plan tab.     */
/* ------------------------------------------------------------------ */

const DRR_WINDOW_PRESETS = [7, 10, 14, 30];
const MIN_DRR_WINDOW = 1;
const MAX_DRR_WINDOW = 365;

export const rateBasisLabel = (b: RateBasis) => (b === 'FORWARD' ? 'Forecast' : 'Past Sales');

export interface RateBasisControlProps {
    metricLabel: string;
    savedBasis: RateBasis;
    effectiveBasis: RateBasis;
    effectiveWindow: number;
    isDirty: boolean;
    saving: boolean;
    onBasisChange: (b: RateBasis) => void;
    onWindowChange: (w: number) => void;
    onSave: () => void;
    onReset: () => void;
    /**
     * Window-only variant: hides the Forecast/Past-Sales basis toggle. Used by views
     * that are past-sales by definition — today the DoH view's per-node pivots, where
     * forecast is stored per channel and has no node grain to split by.
     */
    hideBasis?: boolean;
}

export function RateBasisControl({
    metricLabel,
    savedBasis,
    effectiveBasis,
    effectiveWindow,
    isDirty,
    saving,
    onBasisChange,
    onWindowChange,
    onSave,
    onReset,
    hideBasis,
}: RateBasisControlProps) {
    const [opened, setOpened] = useState(false);
    const isBackward = hideBasis || effectiveBasis === 'BACKWARD';
    // The window is a LENGTH, and the basis is the direction it points in: N days of history
    // behind today, or N days of forecast ahead of it. It used to grey out on Forecast, which
    // left the operator with a rate over a horizon nobody chose and no way to widen it.
    const windowLabel = isBackward ? 'Lookback window' : 'Forecast horizon';
    const buttonLabel = hideBasis
        ? `${metricLabel}: ${effectiveWindow}d window`
        : `${metricLabel}: ${rateBasisLabel(effectiveBasis)} · ${effectiveWindow}d`;

    return (
        <Popover opened={opened} onChange={setOpened} position="bottom-end" width={300} shadow="md" withinPortal>
            <Popover.Target>
                {/* Styled to match the sibling FilterDropdown triggers (Product / Channel / Sort By). */}
                <button
                    type="button"
                    onClick={() => setOpened((o) => !o)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: dataSheetTokens.spacing.xs,
                        height: dataSheetTokens.dimensions.controlHeight,
                        padding: `0 ${dataSheetTokens.spacing.sm}px`,
                        fontSize: 12,
                        fontWeight: 500,
                        color: dataSheetTokens.colors.textPrimary,
                        backgroundColor: dataSheetTokens.colors.surface,
                        border: `1px solid ${opened ? 'rgb(1, 149, 255)' : dataSheetTokens.colors.border}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s ease',
                        boxShadow: opened ? '0 0 0 2px rgba(1, 149, 255, 0.1)' : 'none',
                    }}
                >
                    <span>{buttonLabel}</span>
                    {isDirty && (
                        <span
                            aria-label="unsaved preview"
                            style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: 'rgb(245, 158, 11)',
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: dataSheetTokens.colors.textMuted,
                            transition: 'transform 0.15s ease',
                            transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    >
                        <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                            <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                </button>
            </Popover.Target>
            <Popover.Dropdown>
                <MStack gap="sm">
                    {!hideBasis && (
                        <div>
                            <MText size="xs" fw={600} c="dimmed" mb={4}>Basis</MText>
                            <SegmentedControl
                                fullWidth
                                size="xs"
                                value={effectiveBasis}
                                onChange={(v) => onBasisChange(v as RateBasis)}
                                data={[
                                    { label: 'Forecast', value: 'FORWARD' },
                                    { label: 'Past Sales', value: 'BACKWARD' },
                                ]}
                            />
                        </div>
                    )}
                    <div>
                        <MText size="xs" fw={600} c="dimmed" mb={4}>{windowLabel}</MText>
                        <MGroup gap={4} mb={6}>
                            {DRR_WINDOW_PRESETS.map((p) => (
                                <Button
                                    key={p}
                                    size="compact-xs"
                                    variant={effectiveWindow === p ? 'filled' : 'default'}
                                    onClick={() => onWindowChange(p)}
                                >
                                    {p}
                                </Button>
                            ))}
                        </MGroup>
                        <NumberInput
                            size="xs"
                            min={MIN_DRR_WINDOW}
                            max={MAX_DRR_WINDOW}
                            clampBehavior="strict"
                            allowNegative={false}
                            allowDecimal={false}
                            value={effectiveWindow}
                            suffix=" days"
                            onChange={(v) => {
                                const n = typeof v === 'number' ? v : parseInt(String(v), 10);
                                if (!Number.isNaN(n)) {
                                    onWindowChange(Math.max(MIN_DRR_WINDOW, Math.min(MAX_DRR_WINDOW, n)));
                                }
                            }}
                        />
                    </div>
                    {!hideBasis && (
                        <MText size="xs" c="dimmed">
                            {isBackward
                                ? `Sales over the last ${effectiveWindow} days ÷ ${effectiveWindow}.`
                                : `Forecast for the next ${effectiveWindow} days ÷ ${effectiveWindow}.`}
                        </MText>
                    )}
                    {isDirty && (
                        <MText size="xs" c="dimmed">
                            Preview only. Saved default: {rateBasisLabel(savedBasis)}. Saving updates alerts, reports &amp; planning.
                        </MText>
                    )}
                    <Divider />
                    <MGroup justify="space-between">
                        <Button size="xs" variant="subtle" color="gray" disabled={!isDirty || saving} onClick={onReset}>
                            Reset
                        </Button>
                        <Button size="xs" disabled={!isDirty} loading={saving} onClick={onSave}>
                            Save as default
                        </Button>
                    </MGroup>
                </MStack>
            </Popover.Dropdown>
        </Popover>
    );
}
